import React from 'react';
import type { LeaderboardEntry } from '@/lib/mockData';
import type { Language } from '@/lib/i18n';
import { Crown, Medal } from 'lucide-react';

interface Props {
  entries: LeaderboardEntry[];
  language: Language;
  activeTab: string;
}

const PODIUM_COLORS = ['#F59E0B', '#9CA3AF', '#CD7C2F'];
const PODIUM_HEIGHTS = ['h-28', 'h-20', 'h-16'];
const PODIUM_ICONS = [
  <Crown key="crown" size={18} className="text-amber-500" />,
  <Medal key="medal-silver" size={16} className="text-gray-400" />,
  <Medal key="medal-bronze" size={16} style={{ color: '#CD7C2F' }} />,
];

export default function PodiumSection({ entries, language, activeTab }: Props) {
  if (entries.length < 3) return null;

  // Reorder: 2nd, 1st, 3rd for visual podium
  const podiumOrder = [entries[1], entries[0], entries[2]];
  const podiumRanks = [2, 1, 3];
  const podiumColorIdx = [1, 0, 2];

  return (
    <div className="bg-card rounded-2xl border border-border card-shadow p-6">
      <div className="flex items-end justify-center gap-4">
        {podiumOrder.map((entry, i) => {
          const rank = podiumRanks[i];
          const colorIdx = podiumColorIdx[i];
          const isFirst = rank === 1;

          return (
            <div key={`podium-${entry.id}`} className="flex flex-col items-center gap-2 flex-1 max-w-[140px]">
              {/* Crown/Medal */}
              <div className="mb-1">{PODIUM_ICONS[colorIdx]}</div>

              {/* Avatar */}
              <div
                className={`rounded-full flex items-center justify-center text-white font-700 ${
                  isFirst ? 'w-14 h-14 text-base' : 'w-12 h-12 text-sm'
                }`}
                style={{ backgroundColor: PODIUM_COLORS[colorIdx] }}
              >
                {entry.avatar}
              </div>

              {/* Name */}
              <div className="text-center">
                <p className={`font-700 text-foreground truncate w-full ${isFirst ? 'text-sm' : 'text-xs'}`}>
                  {entry.name.split(' ')[0]}
                </p>
                <p className="text-xs text-muted-foreground font-tabular">
                  {activeTab === 'weekly' ? entry.weeklyXp : entry.xp} XP
                </p>
                <p className="text-xs text-muted-foreground">🔥 {entry.streak}</p>
              </div>

              {/* Podium block */}
              <div
                className={`w-full rounded-t-xl flex items-center justify-center ${PODIUM_HEIGHTS[colorIdx]}`}
                style={{ backgroundColor: `${PODIUM_COLORS[colorIdx]}20`, border: `2px solid ${PODIUM_COLORS[colorIdx]}40` }}
              >
                <span className="text-2xl font-800" style={{ color: PODIUM_COLORS[colorIdx] }}>
                  #{rank}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}